import { useState } from "react";
import { ChevronDown, Download, Shuffle } from "lucide-react";
import ITEMS from "@/data/didYouKnow.json";

const PDF_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_civic-tens/artifacts/ac96d8ae4da9bb8f_roan-did-you-know%20%282%29.pdf";

const Rich = ({ text }) =>
  text.split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") ? <em key={i}>{part.slice(1, -1)}</em> : <span key={i}>{part}</span>
  );

const pickTwo = (exclude = []) => {
  const pool = ITEMS.filter((i) => !exclude.includes(i.id));
  const a = pool[Math.floor(Math.random() * pool.length)];
  const rest = pool.filter((i) => i.id !== a.id);
  return [a, rest[Math.floor(Math.random() * rest.length)]];
};

const Fact = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <article data-testid={`wtf-item-${item.id}`} className={`rounded-2xl border bg-[#1B2A41] p-6 sm:p-7 flex flex-col transition-colors ${open ? "border-[#E2705A]" : "border-[#2C3E55] hover:border-[#8FA58A]"}`}>
      <p className="font-type text-[11px] uppercase tracking-[0.2em] text-[#8FA58A]">{item.pillar}</p>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`wtf-toggle-${item.id}`} aria-expanded={open} className="mt-3 w-full text-left flex items-start gap-3">
        <h3 className="flex-1 text-lg sm:text-xl font-semibold text-[#FDFBF7] leading-snug">
          <span className="font-type text-[#E2705A] mr-2">{String(item.id).padStart(2, "0")}</span>{item.question}
        </h3>
        <ChevronDown className={`w-5 h-5 text-[#8FA58A] flex-shrink-0 mt-1 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <>
          <p className="mt-4 text-sm sm:text-base text-[#FDFBF7]/85 leading-relaxed" data-testid={`wtf-answer-${item.id}`}><Rich text={item.answer} /></p>
          <p className="mt-5 pt-4 border-t border-[#2C3E55] font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Source · <span className="normal-case tracking-normal text-[#FDFBF7]/60">{item.source}</span></p>
        </>
      ) : (
        <p className="mt-4 font-type text-[11px] uppercase tracking-wider text-[#E2705A]/80">Tap to reveal the answer</p>
      )}
    </article>
  );
};

export const WtfSection = () => {
  const [pair, setPair] = useState(() => pickTwo());
  return (
    <section id="wtf" className="bg-[#121E2B] relative grain py-16 sm:py-20" data-testid="wtf-section">
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <p className="font-type text-xs uppercase tracking-[0.3em] text-[#8FA58A] mb-2">Did you know?</p>
            <h2 className="font-type font-bold text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7] leading-none">
              WTF <span className="text-[#E2705A]">—</span> What the Factual
            </h2>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-[#FDFBF7]/75 leading-relaxed">
              Two of {ITEMS.length} things about how Australia is actually governed — each anchored to a statute, case or report you can check yourself. A different pair every visit.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 self-start lg:self-auto">
            <button type="button" onClick={() => setPair(pickTwo(pair.map((p) => p.id)))} data-testid="wtf-shuffle" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-[#8FA58A]/60 text-[#FDFBF7] hover:border-[#E2705A] hover:text-[#E2705A] font-type font-bold text-xs uppercase tracking-wider transition-colors whitespace-nowrap">
              <Shuffle className="w-4 h-4" /> Two more
            </button>
            <a href={PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="wtf-download-pdf" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#E2705A] hover:bg-[#D35D46] text-[#FDFBF7] font-type font-bold text-xs uppercase tracking-wider transition-colors whitespace-nowrap">
              <Download className="w-4 h-4" /> All {ITEMS.length} (PDF)
            </a>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 items-start" data-testid="wtf-list">
          {pair.map((item) => <Fact key={item.id} item={item} />)}
        </div>
      </div>
    </section>
  );
};
